import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import type { ReportRuntimeLifecycle } from './reportPlan';

export interface ReportRuntimeRun {
  siteId: string;
  reportTypeId: string;
  startedAt: string;
  progress: number;
  progressStage: string;
}

export interface ReportRuntimeOutcome {
  siteId: string;
  reportTypeId: string;
  finishedAt: string;
  status?: 'succeeded' | 'failed' | 'cancelled';
  errorCode?: string;
  terminalReason?: string;
  blockedByProviderCoverage?: boolean;
}

function timestamp(value: string | null | undefined): number {
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function reportRevisionTimestamp(report: SiteRagReport): number {
  return Math.max(
    timestamp(report.createdAt),
    timestamp(report.updatedAt),
    timestamp(report.analysisRevision?.createdAt),
  );
}

function isNonGeneratableReportType(type: ReportTypeId): boolean {
  if (type === 'executive-summary') return true;
  return REPORT_TYPE_REGISTRY[type]?.status === 'retired';
}

/**
 * Project live and terminal report jobs onto the cockpit lifecycle.
 *
 * The newest terminal outcome for a report type supersedes every older
 * outcome. Only a latest failure newer than the latest persisted artifact is
 * actionable; a later success/cancellation clears an earlier failure.
 */
export function deriveReportRuntimeByType(input: {
  siteId: string;
  reports: readonly SiteRagReport[];
  liveRuns: readonly ReportRuntimeRun[];
  outcomes: readonly ReportRuntimeOutcome[];
}): Partial<Record<ReportTypeId, ReportRuntimeLifecycle>> {
  const runtime: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>> = {};
  const latestReportTime = new Map<ReportTypeId, number>();
  const latestRunByType = new Map<ReportTypeId, ReportRuntimeRun>();
  const latestOutcomeByType = new Map<ReportTypeId, ReportRuntimeOutcome>();

  for (const report of input.reports) {
    const type = (report.reportType ?? 'feasibility') as ReportTypeId;
    latestReportTime.set(
      type,
      Math.max(latestReportTime.get(type) ?? 0, reportRevisionTimestamp(report)),
    );
  }

  for (const run of input.liveRuns) {
    if (run.siteId !== input.siteId) continue;
    const type = run.reportTypeId as ReportTypeId;
    if (isNonGeneratableReportType(type)) continue;
    const previous = latestRunByType.get(type);
    if (!previous || timestamp(run.startedAt) >= timestamp(previous.startedAt)) {
      latestRunByType.set(type, run);
    }
  }

  for (const [type, run] of latestRunByType) {
    runtime[type] =
      run.progress <= 0 && /queue/i.test(run.progressStage)
        ? 'queued'
        : 'generating';
  }

  for (const outcome of input.outcomes) {
    if (outcome.siteId !== input.siteId) continue;
    const type = outcome.reportTypeId as ReportTypeId;
    if (isNonGeneratableReportType(type)) continue;
    const previous = latestOutcomeByType.get(type);
    if (!previous || timestamp(outcome.finishedAt) >= timestamp(previous.finishedAt)) {
      latestOutcomeByType.set(type, outcome);
    }
  }

  for (const [type, outcome] of latestOutcomeByType) {
    // A live run is the newest actionable state for this type.
    if (runtime[type] === 'queued' || runtime[type] === 'generating') continue;
    if (timestamp(outcome.finishedAt) <= (latestReportTime.get(type) ?? 0)) continue;
    if (outcome.status === 'failed') {
      if (outcome.blockedByProviderCoverage || outcome.errorCode === 'PROVIDER_COVERAGE_BLOCKED') {
        runtime[type] = 'failed-coverage';
      } else {
        runtime[type] = 'failed';
      }
    }
    // succeeded/cancelled/legacy-unknown deliberately leave no runtime marker:
    // the persisted report plan decides Current/Ready from authoritative data.
  }

  return runtime;
}

/**
 * A live job can coexist with an older failed semantic refresh pointer until
 * the new result commits. Preserve active work from either source; a stale
 * terminal pointer must not expose Retry while the accepted job is running.
 * These operational states do not establish report currency or decision readiness.
 */
export function mergeDecisionRuntimeByType(
  reportRuntime: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>,
  analysisRuntime: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>,
): Partial<Record<ReportTypeId, ReportRuntimeLifecycle>> {
  const runtime = { ...reportRuntime, ...analysisRuntime };
  for (const type of Object.keys(reportRuntime) as ReportTypeId[]) {
    const state = reportRuntime[type];
    if (state === 'queued' || state === 'generating') runtime[type] = state;
  }
  return runtime;
}
